import React, { useState, useEffect, useContext } from "react"
import {
  Button,
  Col,
  Form,
  Input,
  Label,
  Row,
  UncontrolledAlert,
  Card,
  CardBody,
  Modal,
} from "reactstrap"
import TenantConnectionContext from "./TenantConnectionContext"
import LoadingOverlay from "react-loading-overlay"
import Error from "pages/NotificationModal/Error"
import Success from "./Success"
import Confirmation from "./Confirmation"
import SecretInput from "./SecretInput"

// 3G (3GTMS) outbound auth is api_type-discriminated. The outbound module
// (tms_ob_sts_update_3gtms) is config-first with a basic-auth fallback and
// supports two mutually-exclusive modes (mirrors the backend auth_schema 3G spec):
//   * Per-status URLs — config.sts_urls carries a full webhook URL per appointment
//     status; each URL embeds its own credentials as query params (so each is
//     treated as a secret: masked on read, revealed on demand).
//   * Basic — a single base URL + username/password columns; the module appends
//     workflow/docType itself.
// Because the module treats ANY non-empty config as sts_urls mode, the two modes
// are exclusive: saving one clears the other's fields.
const THREEG_MODE_STS_URLS = "sts_urls"
const THREEG_MODE_BASIC = "basic"
const THREEG_MODES = [
  { value: THREEG_MODE_STS_URLS, label: "Per-status URLs (credentials in URL)" },
  { value: THREEG_MODE_BASIC, label: "Basic (base URL + username / password)" },
]
const THREEG_STATUS_CODES = [
  { code: "QPND", label: "QPND — Pending" },
  { code: "QREQ", label: "QREQ — Requested" },
  { code: "QISU", label: "QISU — Exception" },
  { code: "QCON", label: "QCON — Confirmed" },
]

// Matches the exact masked shapes auth_schema emits ("*", "**", "X**Y"); a masked
// value on submit means "keep the stored secret".
const isMasked = v => typeof v === "string" && /^(\*{1,2}|.\*\*.)$/.test(v)
const hasVal = v => v != null && String(v).trim() !== ""

const ThreeGAuthForm = ({ hideForm }) => {
  const {
    loading,
    success,
    error,
    errorMessage,
    setSuccess,
    setError,
    tenantData,
    submitUpdate,
    revealSecret,
    updateResponse,
  } = useContext(TenantConnectionContext)

  const [mode, setMode] = useState(THREEG_MODE_BASIC)
  const [stsUrls, setStsUrls] = useState({})
  const [url, setUrl] = useState("")
  const [username, setUsername] = useState("")
  const [password, setPassword] = useState("")
  const [errorMsg, setErrorMsg] = useState("")
  const [showConfirmation, setShowConfirmation] = useState(false)

  useEffect(() => {
    if (!tenantData) return
    setMode(tenantData.threeg_mode || THREEG_MODE_BASIC)
    setStsUrls({ ...(tenantData.sts_urls || {}) })
    setUrl(tenantData.url || "")
    setUsername(tenantData.username || "")
    setPassword(tenantData.password || "")
  }, [tenantData])

  const setStsUrl = (code, value) =>
    setStsUrls(prev => ({ ...prev, [code]: value }))

  const fieldRow = (label, node) => (
    <Row className="mb-2" key={label}>
      <Label className="col-sm-3 col-form-label">{label}</Label>
      <Col sm={6} className="ms-4">
        {node}
      </Col>
    </Row>
  )

  const handleSubmit = () => {
    setErrorMsg("")
    if (mode === THREEG_MODE_STS_URLS) {
      const missing = THREEG_STATUS_CODES.filter(
        s => !(hasVal(stsUrls[s.code]) || isMasked(stsUrls[s.code]))
      ).map(s => s.code)
      if (missing.length) {
        setErrorMsg(`Webhook URL required for: ${missing.join(", ")}`)
        return
      }
    } else {
      if (!hasVal(url)) {
        setErrorMsg("Base URL is required")
        return
      }
      if (!hasVal(username)) {
        setErrorMsg("Username is required")
        return
      }
      if (!(hasVal(password) || isMasked(password))) {
        setErrorMsg("Password is required")
        return
      }
    }
    setShowConfirmation(true)
  }

  const handleConfirmSubmit = async () => {
    setShowConfirmation(false)
    if (mode === THREEG_MODE_STS_URLS) {
      const sts_urls = {}
      THREEG_STATUS_CODES.forEach(s => {
        // preserve the masked placeholder ("keep stored"); trim real edits
        const v = stsUrls[s.code]
        sts_urls[s.code] = isMasked(v) ? v : (v || "").trim()
      })
      await submitUpdate({ threeg_mode: THREEG_MODE_STS_URLS, sts_urls })
    } else {
      await submitUpdate({
        threeg_mode: THREEG_MODE_BASIC,
        url,
        username,
        password,
      })
    }
  }

  const handleCancel = () => {
    setShowConfirmation(false)
    hideForm()
  }
  const closeSuccess = () => {
    setSuccess(false)
    hideForm()
  }
  const togError = () => setError(false)

  if (!tenantData) {
    return (
      <LoadingOverlay active={loading} spinner>
        <div style={{ textAlign: "center", padding: "40px" }}>
          <h5>Loading...</h5>
        </div>
      </LoadingOverlay>
    )
  }

  return (
    <>
      <Modal isOpen={success} toggle={closeSuccess} centered size="md">
        <Success data={updateResponse} cancel={closeSuccess} />
      </Modal>
      <Modal isOpen={error} toggle={togError} centered size="sm">
        <Error
          message={errorMessage || "Something went wrong"}
          closeModal={togError}
        />
      </Modal>
      <Modal
        isOpen={showConfirmation}
        toggle={() => setShowConfirmation(false)}
        centered
        size="sm"
      >
        <Confirmation
          message="Do you want to save these changes?"
          confirm={handleConfirmSubmit}
          cancel={() => setShowConfirmation(false)}
        />
      </Modal>

      <LoadingOverlay active={loading} spinner>
        <Card className="p-3">
          <div className="mb-3">
            <h6 className="text-primary">Edit 3GTMS (3G) Outbound Auth</h6>
          </div>
          <CardBody className="p-0">
            <Card outline color="primary" className="border mt-2 mb-3">
              <CardBody className="p-3">
                {errorMsg && (
                  <div className="mb-3">
                    <UncontrolledAlert color="danger" role="alert">
                      {errorMsg}
                    </UncontrolledAlert>
                  </div>
                )}
                <Form>
                  {fieldRow(
                    "Connection Mode *",
                    <Input
                      type="select"
                      value={mode}
                      onChange={e => setMode(e.target.value)}
                    >
                      {THREEG_MODES.map(m => (
                        <option key={m.value} value={m.value}>
                          {m.label}
                        </option>
                      ))}
                    </Input>
                  )}

                  {mode === THREEG_MODE_STS_URLS ? (
                    <>
                      <Row className="mb-1 mt-3">
                        <Col sm={9} className="ms-4">
                          <small className="text-muted">
                            Per-status webhook URLs — each 3GTMS status posts to its
                            own endpoint, with the username/password embedded in the
                            URL. Reveal (👁) to view or replace the full URL.
                          </small>
                        </Col>
                      </Row>
                      {THREEG_STATUS_CODES.map(s =>
                        fieldRow(
                          s.label,
                          <SecretInput
                            value={stsUrls[s.code] || ""}
                            onChange={v => setStsUrl(s.code, v)}
                            label={`${s.code} URL`}
                            placeholder={`https://… (${s.code})`}
                            revealPath={`sts_urls.${s.code}`}
                            onReveal={revealSecret}
                            multiline
                          />
                        )
                      )}
                    </>
                  ) : (
                    <>
                      {fieldRow(
                        "Base URL",
                        <Input
                          type="text"
                          value={url}
                          placeholder="https://hub2.3gtms.com/xdoc/rest/processDocumentSynchronously"
                          onChange={e => setUrl(e.target.value)}
                        />
                      )}
                      {fieldRow(
                        "Username",
                        <Input
                          type="text"
                          value={username}
                          placeholder="Enter username"
                          onChange={e => setUsername(e.target.value)}
                        />
                      )}
                      {fieldRow(
                        "Password",
                        <SecretInput
                          value={password}
                          onChange={setPassword}
                          label="Password"
                          placeholder="Enter password"
                          revealPath="password"
                          onReveal={revealSecret}
                        />
                      )}
                    </>
                  )}
                </Form>
              </CardBody>
            </Card>

            <div className="d-flex justify-content-end mt-3 me-4">
              <div className="d-flex gap-2">
                <Button onClick={handleSubmit} className="btn" color="primary">
                  Save
                </Button>
                <Button
                  onClick={handleCancel}
                  className="btn"
                  color="secondary"
                >
                  Cancel
                </Button>
              </div>
            </div>
          </CardBody>
        </Card>
      </LoadingOverlay>
    </>
  )
}

export default ThreeGAuthForm
